import { ref, computed } from '@vue/composition-api'
import { constants as c } from '@kingpin-global/kingpin-utils-frontend'
import { apiToastSuccess } from '@/@core/utils/toast'
import analytics from '@/@core/utils/analytics'
import { formatObject } from '@/@core/utils/utils'
import constants from '@/constants'
import store from '@/store'
import { UPDATE_ENTITY, FETCH_PROFILE } from '@/store/modules/auth.module'

const { ADDRESSES_TYPE } = c

export function useAddresses() {
  const billingAddress = ref([])
  const shippingAddress = ref([])
  // Derived view of both lists. Exposed for read-only consumers (e.g. API payload).
  const allAddresses = computed(() => [...billingAddress.value, ...shippingAddress.value])

  const isNotEntityAdmin = computed(() => !store.getters.isEntityAdmin)

  function newAddress(type, isPrimary = false) {
    return {
      addressLine1: '',
      addressLine2: '',
      city: '',
      country: '',
      zipcode: '',
      contactName: '',
      contactNumber: '',
      contactEmail: '',
      isPrimary,
      addressType: type,
      isEditEnabled: true,
      isSaving: false,
    }
  }

  function stripInternalFields(address) {
    const clone = { ...address }
    delete clone.isEditEnabled
    delete clone.isSaving
    delete clone._id
    return clone
  }

  function refFor(type) {
    return type === ADDRESSES_TYPE.BILLING ? billingAddress : shippingAddress
  }

  function buildEmptyState(type, disabled, drafts, keepDrafts) {
    if (keepDrafts && drafts.length > 0) return [drafts[0]]
    const empty = newAddress(type, true)
    empty.isEditEnabled = !disabled
    return [empty]
  }

  function buildTypeState(freshAddresses, type, disabled, keepDrafts) {
    const currentList = refFor(type).value
    const editingIds = new Set(
      currentList.filter(a => a._id && a.isEditEnabled).map(a => a._id),
    )
    const drafts = currentList.filter(a => !a._id)

    const ofType = (freshAddresses || []).filter(a => a?.addressType === type)
    const cloned = JSON.parse(JSON.stringify(ofType)).map(a => ({
      ...a,
      isEditEnabled: editingIds.has(a._id),
      isSaving: false,
    }))

    if (cloned.length === 0) return buildEmptyState(type, disabled, drafts, keepDrafts)
    if (keepDrafts && drafts.length > 0) return [...cloned, ...drafts]
    return cloned
  }

  // Scoped init: when `options.type` is set, only that list is rebuilt and
  // the other list's ref stays untouched. When `options.discardDrafts` is
  // true, in-memory drafts for the updated type are dropped — used right
  // after a new address of that type is saved so the server version replaces
  // the pre-save draft without leaving a stale blank form behind.
  function initAddresses(addresses, disabled, options = {}) {
    const { type = null, discardDrafts = false } = options
    const touchBilling = type === null || type === ADDRESSES_TYPE.BILLING
    const touchShipping = type === null || type === ADDRESSES_TYPE.SHIPPING

    if (touchBilling) {
      billingAddress.value = buildTypeState(
        addresses,
        ADDRESSES_TYPE.BILLING,
        disabled,
        !discardDrafts,
      )
    }
    if (touchShipping) {
      shippingAddress.value = buildTypeState(
        addresses,
        ADDRESSES_TYPE.SHIPPING,
        disabled,
        !discardDrafts,
      )
    }
  }

  // --- address CRUD ---

  function trackAddressChange(address, addresses) {
    const event = address._id
      ? constants.TRACKS.ACTIONS.UPDATE_ADDRESS_PROFILE
      : constants.TRACKS.ACTIONS.CREATE_ADDRESS_PROFILE
    analytics.track(event, formatObject({ addresses }))
  }

  function buildApiPayload(savedAddress) {
    const current = [...billingAddress.value, ...shippingAddress.value]
    // Drop the pre-save version of this address (matched by _id, or by
    // reference for new drafts) so the saved copy replaces it exactly once.
    const others = current.filter(a => {
      if (a === savedAddress) return false
      if (savedAddress._id && a._id === savedAddress._id) return false
      return true
    })
    return [...others, savedAddress]
      .filter(a => a._id || a.addressLine1)
      .map(stripInternalFields)
  }

  function addOrUpdateAddress(address) {
    address.isSaving = true
    const payload = buildApiPayload(address)
    trackAddressChange(address, payload)
    const isNewAddress = !address._id
    const savedType = address.addressType
    return store.dispatch(UPDATE_ENTITY, { addresses: payload })
      .then(() => {
        apiToastSuccess('Address updated successfully')
        return store.dispatch(FETCH_PROFILE, true).then(res => {
          const freshAddresses = res?.addresses || []
          initAddresses(freshAddresses, false, {
            type: savedType,
            discardDrafts: isNewAddress,
          })
        })
      })
      .catch(() => { })
  }

  function setAsPrimaryAddress(addressId) {
    const current = [...billingAddress.value, ...shippingAddress.value]
    const cloned = JSON.parse(JSON.stringify(current))
    cloned.forEach(add => {
      if (add.addressType === ADDRESSES_TYPE.SHIPPING) {
        add.isPrimary = add._id === addressId
      }
    })
    const payload = cloned.filter(a => a._id).map(stripInternalFields)
    return store.dispatch(UPDATE_ENTITY, { addresses: payload })
      .then(() => store.dispatch(FETCH_PROFILE, true).then(res => {
        const freshAddresses = res?.addresses || []
        initAddresses(freshAddresses, false, { type: ADDRESSES_TYPE.SHIPPING })
      }))
      .catch(err => { throw err })
  }

  function removeAddress(address) {
    const current = [...billingAddress.value, ...shippingAddress.value]
    const filtered = current.filter(a => a._id !== address._id)
    const payload = filtered.filter(a => a._id || a.addressLine1).map(stripInternalFields)
    return store.dispatch(UPDATE_ENTITY, { addresses: payload })
      .then(() => {
        apiToastSuccess('Address removed successfully')
        return store.dispatch(FETCH_PROFILE, true).then(res => {
          const freshAddresses = res?.addresses || []
          initAddresses(freshAddresses, false, { type: ADDRESSES_TYPE.SHIPPING })
        })
      })
      .catch(() => { })
  }

  return {
    billingAddress,
    shippingAddress,
    allAddresses,
    isNotEntityAdmin,
    ADDRESSES_TYPE,
    initAddresses,
    addOrUpdateAddress,
    setAsPrimaryAddress,
    removeAddress,
    newAddress,
  }
}
